var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { VIDEO_QUALITY_LEVELS } from '../../video-quality/constants';
import { isMobileBrowser } from '../environment/utils';
import MeetHourJS, { MHTrackErrors, browser } from '../lib-meet-hour';
import { gumPending, setAudioMuted } from '../media/actions';
import { MEDIA_TYPE } from '../media/constants';
import { getStartWithAudioMuted } from '../media/functions';
import { IGUMPendingState } from '../media/types';
import { toState } from '../redux/functions';
import { getUserSelectedCameraDeviceId, getUserSelectedMicDeviceId } from '../settings/functions.web';
import { getCameraFacingMode } from './functions';
import loadEffects from './loadEffects';
import logger from './logger';
import { showWarningNotification } from '../../notifications';
export * from './functions';
let _lastExpectedTrackIssueNotice = {
    at: 0,
    key: ''
};
/**
 * Maps expected local device errors to user-facing notification keys.
 *
 * @param {Error} error - Track creation error.
 * @returns {string}
 */
function _getExpectedTrackIssueMessageKey(error) {
    var _a;
    const name = error === null || error === void 0 ? void 0 : error.name;
    const details = `${(error === null || error === void 0 ? void 0 : error.message) || ''} ${((_a = error === null || error === void 0 ? void 0 : error.gum) === null || _a === void 0 ? void 0 : _a.message) || ''}`.toLowerCase();
    if (name === MHTrackErrors.TIMEOUT) {
        return 'notify.deviceBusy';
    }
    if (name === MHTrackErrors.PERMISSION_DENIED) {
        return 'notify.devicePermissionBlocked';
    }
    if (name === MHTrackErrors.NOT_FOUND) {
        return 'notify.deviceNotFound';
    }
    if (name === MHTrackErrors.DEVICE_BUSY) {
        // lib-meet-hour maps NotReadableError / TrackStartError here after its
        // built-in single-shot retry has already failed - i.e. the OS still
        // cannot hand the device to getUserMedia (another app is genuinely
        // holding it). The user-facing text is the same as the
        // GENERAL + "in use" message branch below.
        return 'notify.deviceBusy';
    }
    if (name === MHTrackErrors.GENERAL && /(busy|in use|already|not readable|could not start|starting video source)/.test(details)) {
        return 'notify.deviceBusy';
    }
    return '';
}
/**
 * Displays throttled user notifications for expected device issues.
 *
 * @param {Error} error - Track creation error.
 * @returns {boolean}
 */
function _notifyExpectedTrackIssue(error) {
    var _a;
    if ((error === null || error === void 0 ? void 0 : error.name) === MHTrackErrors.SCREENSHARING_USER_CANCELED) {
        return true;
    }
    const descriptionKey = _getExpectedTrackIssueMessageKey(error);
    if (!descriptionKey || typeof APP === 'undefined' || !((_a = APP === null || APP === void 0 ? void 0 : APP.store) === null || _a === void 0 ? void 0 : _a.dispatch)) {
        return false;
    }
    const key = `${(error === null || error === void 0 ? void 0 : error.name) || 'unknown'}:${descriptionKey}`;
    const now = Date.now();
    if (_lastExpectedTrackIssueNotice.key === key && now - _lastExpectedTrackIssueNotice.at < 10000) {
        return true;
    }
    _lastExpectedTrackIssueNotice = {
        at: now,
        key
    };
    APP.store.dispatch(showWarningNotification({
        titleKey: 'notify.deviceAccessIssueTitle',
        descriptionKey
    }));
    return true;
}
/**
 * Create local tracks of specific types.
 *
 * @param {Object} options - The options with which the local tracks are to be
 * created.
 * @param {string|null} [options.cameraDeviceId] - Camera device id or
 * {@code undefined} to use app's settings.
 * @param {string[]} options.devices - Required track types such as 'audio'
 * and/or 'video'.
 * @param {string|null} [options.micDeviceId] - Microphone device id or
 * {@code undefined} to use app's settings.
 * @param {number|undefined} [oprions.timeout] - A timeout for MeetHourJS.createLocalTracks used to create the tracks.
 * @param {boolean} [options.firePermissionPromptIsShownEvent] - Whether lib-meet-hour
 * should check for a {@code getUserMedia} permission prompt and fire a
 * corresponding event.
 * @param {IStore} store - The redux store in the context of which the function
 * is to execute and from which state such as {@code config} is to be retrieved.
 * @returns {Promise<MHLocalTrack[]>}
 */
export function createLocalTracksF(options = {}, store) {
    var _a, _b, _c;
    let { cameraDeviceId, micDeviceId } = options;
    const { desktopSharingSourceDevice, desktopSharingSources, firePermissionPromptIsShownEvent, timeout } = options;
    // TODO The app's settings should go in the redux store and then the
    // reliance on the global variable APP will go away.
    const resolvedStore = store || APP.store;
    const state = resolvedStore.getState();
    if (typeof cameraDeviceId === 'undefined' || cameraDeviceId === null) {
        cameraDeviceId = getUserSelectedCameraDeviceId(state);
    }
    if (typeof micDeviceId === 'undefined' || micDeviceId === null) {
        micDeviceId = getUserSelectedMicDeviceId(state);
    }
    const { desktopSharingFrameRate, firefox_fake_device, resolution: configResolution } = state['features/base/config'];
    const constraints = (_a = options.constraints) !== null && _a !== void 0 ? _a : state['features/base/config'].constraints;
    // The user's video-quality preference, when explicitly picked (SD/HD/LD), wins over the static
    // config.resolution. Without this, turning the camera on (or reopening it via filter / device switch)
    // would always reopen at config.resolution (typically HD), making a previously-applied narrowing to SD
    // silently revert the moment a track is recreated. AUTO falls through to config — that case still means
    // "use whatever the platform default is". The heights VIDEO_QUALITY_LEVELS carries (720/480/360/240) are
    // all keys of the lib-meet-hour `Resolutions` table, so each is an exact match with no snapping.
    // Caller-supplied resolution (e.g. the capture-reacquire path) still wins over both.
    const preferredVideoQuality = (_b = state['features/video-quality']) === null || _b === void 0 ? void 0 : _b.preferredVideoQuality;
    // `preferredVideoQuality` is already a frame height - VIDEO_QUALITY_LEVELS maps names *to* heights
    // (STANDARD -> 480) - so it is compared and used directly. It must not be used to index that map:
    // `VIDEO_QUALITY_LEVELS[480]` is always undefined, which silently switched this whole branch off and
    // sent every recreated track straight back to config.resolution.
    const explicitQuality = typeof preferredVideoQuality === 'number'
        && preferredVideoQuality !== VIDEO_QUALITY_LEVELS.AUTO
        && Object.values(VIDEO_QUALITY_LEVELS).includes(preferredVideoQuality);
    const resolution = (_c = options.resolution) !== null && _c !== void 0 ? _c : (explicitQuality ? preferredVideoQuality : configResolution);
    return (loadEffects(resolvedStore).then((effectsArray) => {
        var _a;
        // Filter any undefined values returned by Promise.resolve().
        const effects = effectsArray.filter(effect => Boolean(effect));
        return MeetHourJS.createLocalTracks({
            cameraDeviceId,
            constraints,
            desktopSharingFrameRate,
            desktopSharingSourceDevice,
            desktopSharingSources,
            // Copy array to avoid mutations inside library.
            devices: (_a = options.devices) === null || _a === void 0 ? void 0 : _a.slice(0),
            effects,
            facingMode: options.facingMode || getCameraFacingMode(state),
            firefox_fake_device,
            firePermissionPromptIsShownEvent,
            micDeviceId,
            resolution,
            timeout
        })
            .catch((err) => {
            if (_notifyExpectedTrackIssue(err)) {
                logger.warn('Failed to create local tracks', err);
            }
            else {
                logger.error('Failed to create local tracks', err);
            }
            return Promise.reject(err);
        });
    }));
}
/**
 * Returns an object containing a promise which resolves with the created tracks and the errors resulting from that
 * process.
 *
 * @returns {Promise<MHLocalTrack[]>}
 *
 * @todo Refactor to not use APP.
 */
export function createPrejoinTracks() {
    const errors = {};
    const initialDevices = [MEDIA_TYPE.AUDIO];
    const requestedAudio = true;
    let requestedVideo = false;
    const { startAudioOnly, startWithVideoMuted } = APP.store.getState()['features/base/settings'];
    const startWithAudioMuted = getStartWithAudioMuted(APP.store.getState());
    // On Electron there is no permission prompt for granting permissions. That's why we don't need to
    // spend much time displaying the overlay screen. If GUM is not resolved within 15 seconds it will
    // probably never resolve.
    const timeout = browser.isElectron() ? 15000 : 60000;
    // Always get a handle on the audio input device so that we have statistics even if the user joins the
    // conference muted. Previous implementation would only acquire the handle when the user first unmuted,
    // which would results in statistics ( such as "No audio input" or "Are you trying to speak?") being available
    // only after that point.
    if (startWithAudioMuted) {
        APP.store.dispatch(setAudioMuted(true));
    }
    if (!startWithVideoMuted && !startAudioOnly) {
        initialDevices.push(MEDIA_TYPE.VIDEO);
        requestedVideo = true;
    }
    let tryCreateLocalTracks = Promise.resolve([]);
    const { dispatch } = APP.store;
    dispatch(gumPending(initialDevices, IGUMPendingState.PENDING_UNMUTE));
    if (requestedAudio || requestedVideo) {
        tryCreateLocalTracks = createLocalTracksF({
            devices: initialDevices,
            firePermissionPromptIsShownEvent: true,
            timeout
        }, APP.store)
            .catch((err) => __awaiter(this, void 0, void 0, function* () {
            if (err.name === MHTrackErrors.TIMEOUT && !browser.isElectron()) {
                errors.audioAndVideoError = err;
                return [];
            }
            // Retry with separate gUM calls.
            const gUMPromises = [];
            const tracks = [];
            if (requestedAudio) {
                gUMPromises.push(createLocalTracksF({
                    devices: [MEDIA_TYPE.AUDIO],
                    firePermissionPromptIsShownEvent: true,
                    timeout
                }));
            }
            if (requestedVideo) {
                gUMPromises.push(createLocalTracksF({
                    devices: [MEDIA_TYPE.VIDEO],
                    firePermissionPromptIsShownEvent: true,
                    timeout
                }));
            }
            const results = yield Promise.allSettled(gUMPromises);
            let errorMsg;
            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') {
                    tracks.push(result.value[0]);
                }
                else {
                    errorMsg = result.reason;
                    const isAudio = idx === 0;
                    logger.error(`${isAudio ? 'Audio' : 'Video'} track creation failed with error ${errorMsg}`);
                    if (isAudio) {
                        errors.audioOnlyError = errorMsg;
                    }
                    else {
                        errors.videoOnlyError = errorMsg;
                    }
                }
            });
            if (errors.audioOnlyError && errors.videoOnlyError) {
                errors.audioAndVideoError = errorMsg;
            }
            return tracks;
        }))
            .finally(() => {
            dispatch(gumPending(initialDevices, IGUMPendingState.NONE));
        });
    }
    return {
        tryCreateLocalTracks,
        errors
    };
}
/**
 * Determines whether toggle camera should be enabled or not.
 *
 * @param {Function|Object} stateful - The redux store or {@code getState} function.
 * @returns {boolean} - Whether toggle camera should be enabled.
 */
export function isToggleCameraEnabled(stateful) {
    const state = toState(stateful);
    const { videoInput } = state['features/base/devices'].availableDevices;
    return isMobileBrowser() && Number(videoInput === null || videoInput === void 0 ? void 0 : videoInput.length) > 1;
}
